import { Link } from "react-router-dom";
import { AlertTriangle, Check, Phone, SearchCheck, Wrench } from "lucide-react";
import SEO from "../components/SEO";
import EditorialMarquee from "../components/EditorialMarquee";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import { PHONE_DISPLAY, PHONE_FIXE_DISPLAY, PHONE_FIXE_TEL, PHONE_TEL } from "../data/site";

const QUALITES = [
    "Des compétences solides en pose et remplacement de serrures, blindage de portes, volets et rideaux métalliques — acquises par la formation et l'expérience terrain",
    "Le remplacement ou le forçage n'intervient qu'après avoir épuisé toutes les techniques de dépannage connues",
    "Des tarifs de dépannage acceptables et pas chers, annoncés avant l'intervention",
    "Des équipements et outils sophistiqués pour intervenir avec le moindre dégât et coût",
    "L'écoute du client et son assistance en cas d'urgence",
    "Une disponibilité 24h/24, 7j/7, même les jours fériés",
    "Une intervention sur les lieux le plus rapidement possible — environ une demi-heure",
];

const SIGNAUX_ALARME = [
    {
        signal: "Prix anormalement bas affiché",
        explication: "Une annonce « ouverture de porte dès 19 € » sur Google ou un flyer n'inclut pas la main-d'œuvre réelle. Le prix final annoncé sur place est souvent 5 à 10 fois supérieur. Un serrurier honnête affiche un prix complet incluant la main-d'œuvre.",
    },
    {
        signal: "Pas de devis écrit avant l'intervention",
        explication: "Pour un dépannage à domicile, le professionnel doit vous remettre un devis écrit avant d'intervenir (voir les fiches pratiques de la DGCCRF). Refuser de signer est votre droit. Un artisan qui commence à travailler sans votre accord écrit commet une infraction.",
    },
    {
        signal: "Pas de numéro SIRET ou d'adresse",
        explication: "Tout artisan doit pouvoir fournir son numéro SIRET (vérifiable sur le site de l'INPI) et une adresse physique. Un serrurier qui refuse ou qui est « injoignable » après l'intervention est un signal d'alarme majeur.",
    },
    {
        signal: "Remplacement systématique de toutes les pièces",
        explication: "Un serrurier malhonnête propose systématiquement de changer toute la serrure, même quand le problème se limite au cylindre. Demandez toujours une explication sur pourquoi chaque remplacement est nécessaire.",
    },
    {
        signal: "Absence de facture ou facture incomplète",
        explication: "Toute prestation doit donner lieu à une facture mentionnant SIRET, TVA, adresse, détail des pièces et de la main-d'œuvre séparés. Une facture manuscrite ou sans TVA est irrégulière.",
    },
];

const DROITS_CONSO = [
    {
        titre: "Devis écrit avant toute intervention",
        texte: "La DGCCRF rappelle qu'un devis écrit doit vous être remis avant toute intervention de dépannage à domicile. Le devis doit mentionner la date, les coordonnées de l'artisan, la description de la prestation, le prix des pièces et la main-d'œuvre.",
    },
    {
        titre: "Tarifs encadrés par arrêté préfectoral",
        texte: "La DGCCRF publie chaque année les prix de référence pour les prestations de serrurerie. Ces plafonds orientent le consommateur. Un dépassement significatif sans justification peut être signalé à la DGCCRF (signalconso.gouv.fr) ou à la DDPP de Paris.",
    },
    {
        titre: "Droit de refus avant intervention",
        texte: "Vous pouvez refuser le devis sans frais si l'artisan n'a pas encore commencé à travailler. Un devis de déplacement ne peut être facturé que s'il était expressément prévu et que vous l'avez accepté à l'avance.",
    },
    {
        titre: "Garantie légale sur les pièces posées",
        texte: "Les pièces (cylindres, serrures) sont couvertes par la garantie légale de conformité (2 ans) et la garantie des vices cachés. Notre société applique également une garantie commerciale d'un an sur la main-d'œuvre.",
    },
];

export default function BonSerrurier() {
    return (
        <div data-testid="bon-serrurier-page">
            <SEO
                title="Bon Serrurier Paris — Guide Anti-Arnaque & Serrurier de Confiance"
                description="Bon serrurier Paris : comment le reconnaître et éviter les arnaques — devis obligatoire, tarifs transparents, agrément assurance. Le guide d'un artisan honnête depuis 1985. ☎ 01 84 21 07 67"
                path="/bon-serrurier-paris"
            />

            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span className="text-gold">Bon serrurier Paris</span>
                    </nav>
                    <SectionLabel dark>Guide anti-arnaque</SectionLabel>
                    <StatusBadge className="mt-6" />
                    <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLines delay={0.15} lines={["Qu'est-ce qu'un"]} />
                        <MaskedLines delay={0.45} lines={["bon serrurier à Paris ?"]} lineClassName="text-gold" />
                    </h1>
                    <FadeUp delay={0.55}>
                        <p className="mt-6 max-w-xl leading-relaxed text-white/70">
                            Professionnel de la sécurité des locaux résidentiels, commerciaux et industriels, le
                            serrurier propose dépannage, assistance et intervention d'urgence. Voici notre guide pour
                            bien choisir votre serrurier à Paris — et éviter les arnaques.
                        </p>
                    </FadeUp>
                </div>
            </section>

            <EditorialMarquee dark={false} />

            {/* QUALITÉS */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <div className="grid gap-12 lg:grid-cols-[1fr_1.5fr] lg:gap-20">
                    <FadeUp>
                        <SectionLabel>Les 7 qualités</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            Les qualités d'un bon serrurier
                        </h2>
                        <p className="mt-6 leading-relaxed text-midnight/70">
                            Une solide expérience permet de résoudre la majorité des problèmes de serrurerie, même
                            les plus difficiles — et de vous assister à distance en cas de SOS serrurerie.
                        </p>
                        <div className="mt-8 flex items-center gap-4 rounded-xl bg-midnight p-6 text-white">
                            <Wrench className="h-8 w-8 shrink-0 text-gold" strokeWidth={1.8} />
                            <p className="text-sm leading-relaxed text-white/75">
                                Le meilleur serrurier trouve la solution qui garantit la sécurité de votre local, le
                                prix juste de son intervention et la qualité des pièces remplacées.
                            </p>
                        </div>
                    </FadeUp>
                    <div className="grid gap-px bg-midnight/10" data-testid="bon-qualites">
                        {QUALITES.map((q, i) => (
                            <FadeUp key={q} delay={i * 0.04} className="bg-white">
                                <div className="flex h-full items-start gap-4 px-6 py-5">
                                    <span className="font-display text-2xl font-extrabold text-emergency/30">{String(i + 1).padStart(2, "0")}</span>
                                    <p className="text-sm font-semibold leading-relaxed text-midnight/75">{q}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* OÙ TROUVER */}
            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>Où chercher</SectionLabel>
                        <h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            Où trouver un serrurier de confiance sur Paris ?
                        </h2>
                    </FadeUp>
                    <div className="mt-12 grid gap-5 md:grid-cols-3">
                        <FadeUp>
                            <div className="h-full rounded-xl border border-emergency/20 bg-emergency/5 p-8" data-testid="bon-warning">
                                <AlertTriangle className="h-8 w-8 text-emergency" strokeWidth={1.8} />
                                <h3 className="mt-5 font-display text-xl font-extrabold tracking-tight">
                                    Méfiez-vous des flyers publicitaires
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-midnight/65">
                                    Les prospectus glissés dans les boîtes aux lettres ou sous les portes cachent
                                    parfois des artisans peu sérieux qui facturent trop cher. Pire : certains
                                    cambrioleurs se font passer pour des serruriers afin d'analyser les lieux avant
                                    de revenir commettre leurs vols.
                                </p>
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.08}>
                            <div className="h-full rounded-xl border border-midnight/10 bg-cloud p-8">
                                <SearchCheck className="h-8 w-8 text-emergency" strokeWidth={1.8} />
                                <h3 className="mt-5 font-display text-xl font-extrabold tracking-tight">
                                    Comparez plusieurs devis en ligne
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-midnight/65">
                                    La majorité des serruriers parisiens présentent leurs services et leurs contacts
                                    sur leur site. Un bon serrurier fournit un devis approximatif gratuit et sans
                                    engagement, même sur simple appel. Plusieurs devis collectés vous permettent de
                                    faire le bon choix.
                                </p>
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.16}>
                            <div className="grain h-full rounded-xl bg-midnight p-8 text-white">
                                <Check className="h-8 w-8 text-gold" strokeWidth={1.8} />
                                <h3 className="mt-5 font-display text-xl font-extrabold tracking-tight">
                                    Une équipe ponctuelle, sérieuse, discrète
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-white/70">
                                    Sur simple appel au {PHONE_FIXE_DISPLAY} et après acceptation de notre devis de
                                    dépannage, nous intervenons en moins de 30 minutes dans tous les
                                    arrondissements parisiens.
                                </p>
                                <a href={PHONE_FIXE_TEL} className="mt-5 inline-flex items-center gap-2 font-bold text-gold">
                                    <Phone className="h-4 w-4" /> {PHONE_FIXE_DISPLAY}
                                </a>
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* SIGNAUX D'ALARME */}
            <section className="bg-cloud py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>5 red flags</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            Comment repérer un faux serrurier ?
                        </h2>
                        <p className="mt-6 max-w-3xl leading-relaxed text-midnight/70">
                            Paris concentre un grand nombre d'opérateurs peu scrupuleux qui profitent des situations
                            d'urgence. Ces cinq signaux d'alarme doivent vous inciter à raccrocher immédiatement
                            et à appeler un professionnel certifié.
                        </p>
                    </FadeUp>
                    <div className="mt-10 space-y-4">
                        {SIGNAUX_ALARME.map((item, i) => (
                            <FadeUp key={item.signal} delay={i * 0.06}>
                                <div className="flex gap-5 border border-emergency/20 bg-white p-7">
                                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-emergency" />
                                    <div>
                                        <h3 className="font-display text-lg font-extrabold tracking-tight text-emergency">
                                            {item.signal}
                                        </h3>
                                        <p className="mt-2 leading-relaxed text-sm text-midnight/70">{item.explication}</p>
                                    </div>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* VOS DROITS */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <FadeUp>
                    <SectionLabel>Consommateur protégé</SectionLabel>
                    <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                        Vos droits face à un serrurier
                    </h2>
                    <p className="mt-6 max-w-3xl leading-relaxed text-midnight/70">
                        La réglementation française protège le consommateur contre les pratiques abusives en
                        serrurerie. Connaître vos droits vous met en position de force — même dans une situation
                        d'urgence où vous êtes vulnérable.
                    </p>
                </FadeUp>
                <div className="mt-10 grid gap-5 sm:grid-cols-2">
                    {DROITS_CONSO.map((d, i) => (
                        <FadeUp key={d.titre} delay={i * 0.07}>
                            <div className="h-full border-l-2 border-gold bg-white p-8">
                                <h3 className="font-display text-xl font-extrabold tracking-tight">{d.titre}</h3>
                                <p className="mt-4 text-sm leading-relaxed text-midnight/70">{d.texte}</p>
                            </div>
                        </FadeUp>
                    ))}
                </div>
                <FadeUp delay={0.3}>
                    <div className="mt-10 border border-midnight/10 bg-white p-8 md:p-10">
                        <h3 className="font-display text-2xl font-extrabold tracking-tight">
                            Checklist : validez ces 5 points avant d'accepter l'intervention
                        </h3>
                        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                            {[
                                "L'artisan a fourni son numéro SIRET",
                                "Un devis écrit m'a été remis et signé",
                                "Le prix total (pièces + main-d'œuvre) est clairement indiqué",
                                "La facture sera émise à la fin de l'intervention",
                                "L'artisan est assuré en RC professionnelle",
                            ].map((item, i) => (
                                <div key={item} className="flex items-start gap-3 rounded border border-midnight/10 p-4 text-sm">
                                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                                    <span className="font-semibold text-midnight/80">{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </FadeUp>
            </section>


            {/* CTA */}
            <section className="grain bg-midnight py-16 text-white md:py-20">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
                    <div>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                            Un serrurier honnête, au juste prix.
                        </h2>
                        <p className="mt-3 text-white/60">Devis gratuit et détaillé avant chaque intervention. Agréé assurance.</p>
                    </div>
                    <a
                        href={PHONE_TEL}
                        data-testid="bon-bottom-call-button"
                        className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                    >
                        <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                    </a>
                </div>
            </section>
        </div>
    );
}
